import React, { FC, useState } from 'react';
import { ICartDetailModel } from 'components/Cart/models';
import styles from './SoftwarePlan.module.scss';
import { TextV2 } from 'components/TextV2/TextV2';
import { useTranslation } from 'react-i18next';
import { Image } from 'components/Image/Image';
import { Grid } from 'components/Grid/Grid';
import { useContentfulSoftwarePlansQuery } from './useContentfulSoftwarePlansQuery/useContentfulSoftwarePlansQuery';
import { LinkVariant } from 'api/contentful/contentTypes.cloverdotcom';
import { Button } from 'components/Button/Button';
import { FeaturesModal } from 'components/FeaturesModal/FeaturesModal';
import { useCartContext } from 'components/Cart/hooks/useCartContext';

interface ICartPlansListProps {
  cartData: ICartDetailModel;
}

export const SoftwarePlan: FC<ICartPlansListProps> = ({ cartData }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();
  const { checkout } = useCartContext();
  const { softwarePlan } = cartData;

  const { data: contentfulSoftwarePlans } = useContentfulSoftwarePlansQuery();
  const selectedSoftwarePlan = contentfulSoftwarePlans?.find(plan => plan.dcsId === softwarePlan?.id);
  const hasSelectedSoftwarePlan = selectedSoftwarePlan?.modalFeatures;

  const imageURL = softwarePlan?.imageURL;
  const additionalDeviceFee = t('cart-page.monthly-price', { price: softwarePlan?.additionalDevicePrice });

  return (
    <Grid container item xs={12}>
      {softwarePlan && (
        <Grid item md={12} xs={12} className={styles.cartPlanCell}>
          <Grid item>
            {imageURL && (
              <Image
                src={imageURL}
                aria-hidden="true"
                className={styles.cartPlanCellImage}
                data-testid="software-plan-image"
              />
            )}
          </Grid>
          <Grid item className={styles.cartPlanCellContainer}>
            <Button
              variant={LinkVariant.TEXT}
              className={styles.cartPlanCellButton}
              onClick={selectedSoftwarePlan ? () => setIsOpen(true) : undefined}
            >
              {t('cart-page.plan-name', { planName: softwarePlan.name })}
            </Button>
            {hasSelectedSoftwarePlan && (
              <FeaturesModal
                modalState={[isOpen, () => setIsOpen(!isOpen)]}
                entries={hasSelectedSoftwarePlan}
              />
            )}
            <TextV2
              value={t('cart-page.additional-device-price', { additionalDeviceFee })}
              tagName="p"
              variant="body-small"
              className={styles.cartPlanCellText}
            />
            <TextV2
              value={t('cart-page.card-use', {
                percentage: softwarePlan.transactions.inPerson.percentage,
                price: softwarePlan.transactions.inPerson.cost,
              })}
              tagName="p"
              variant="body-small"
              className={styles.cartPlanCellText}
            />
            <TextV2
              value={t('cart-page.card-info', {
                percentage: softwarePlan.transactions.keyedIn.percentage,
                price: softwarePlan.transactions.keyedIn.cost,
              })}
              tagName="p"
              variant="body-small"
              className={styles.cartPlanCellText}
            />
          </Grid>
          <Grid item>
            <TextV2
              value={t('cart-page.monthly-price', { price: checkout.softwareTotal })}
              tagName="p"
              className={styles.cartPlanCellPricing}
              variant="body"
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};
